import { useIsArchonApp } from '@resources/js/contexts/useIsArchonApp';
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import type { FetchStatus } from '@resources/js/hooks/useFetch';

export type ArchonAppContext = {
  userStatus?: FetchStatus;
  uploaderIsProcessing: boolean;
};

type ArchonAppIntegrationContext = {
  isArchonApp: boolean;
  iframeId: string | null;
  appContext: ArchonAppContext | null;
  triggerSignOut: () => void;
};

const ArchonAppIntegrationContext = createContext<ArchonAppIntegrationContext>({
  isArchonApp: false,
  iframeId: null,
  appContext: null,
  triggerSignOut: () => {},
});

export const ArchonAppIntegrationProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const isArchonApp = useIsArchonApp();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const iframeIdParam = router.query.iframeId as string | undefined;
  const [iframeId, setIframeId] = useState<string | null>(null);
  const [iframeRequiresReloadBeforeReady, setIframeRequiresReloadBeforeReady] =
    useState(false);
  const [appContext, setAppContext] = useState<ArchonAppContext | null>(null);
  const [navigation, setNavigation] = useState<{
    isNavigating: boolean;
    hashToNavigateTo: string | null;
  }>({
    hashToNavigateTo: null,
    isNavigating: false,
  });
  const lastHashChangeKey = useRef<string | null>(null);

  useEffect(() => {
    const setNavigating = () => {
      setNavigation((prev) => ({ ...prev, isNavigating: true }));
    };

    const endNavigating = () => {
      setNavigation((prev) => ({ ...prev, isNavigating: false }));
    };

    router.events.on('routeChangeStart', setNavigating);
    router.events.on('routeChangeComplete', endNavigating);

    return () => {
      router.events.off('routeChangeStart', setNavigating);
      router.events.off('routeChangeComplete', endNavigating);
    };
  }, [router.events]);

  useEffect(() => {
    if (navigation.isNavigating || !navigation.hashToNavigateTo) {
      return;
    }

    const { hashToNavigateTo } = navigation;

    setTimeout(() => {
      try {
        document.getElementById(hashToNavigateTo.slice(1))?.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      } finally {
        setNavigation((prev) => ({
          ...prev,
          hashToNavigateTo: null,
        }));
      }
    }, 100);
  }, [navigation]);

  useEffect(() => {
    if (iframeIdParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIframeId(iframeIdParam);
    }
  }, [iframeIdParam]);

  useEffect(
    function announceCanReceiveMessages() {
      if (!isArchonApp || !iframeId) {
        return;
      }
      setTimeout(() => {
        window.top?.postMessage(
          {
            message: 'iframe-can-receive-messages',
            iframeId,
          },
          '*',
        );
        // A short delay just to make sure event listeners are set up in time
      }, 100);
    },
    [iframeId, isArchonApp],
  );

  useEffect(
    function requestLoginIfArchonApp() {
      if (!isArchonApp || !iframeId || sessionStatus === 'loading') {
        return;
      }
      // We still request login even if sessionStatus === 'authenticated' just in case we're signed in as the wrong user
      window.top?.postMessage(
        {
          message: 'request-login',
          iframeId,
          currentUserId: session?.user.id,
          currentGameSlug: session?.user.gameSlug,
        },
        '*',
      );
    },
    [
      iframeId,
      isArchonApp,
      session?.user.gameSlug,
      session?.user.id,
      sessionStatus,
    ],
  );

  const [signInRequestQueue, setSignInRequestQueue] = useState<
    ArchonAppLoginMessage[]
  >([]);
  const pushSignInRequest = useCallback(
    (request: ArchonAppLoginMessage) =>
      setSignInRequestQueue((prev) => [...prev, request]),
    [],
  );
  const nextSignInRequest = useCallback(
    () =>
      setSignInRequestQueue((prev) => {
        if (prev.length === 0) {
          return prev;
        }
        const [, ...rest] = prev;
        return rest;
      }),
    [],
  );
  const signInRequest = signInRequestQueue[0] ?? null;

  const triggerSignOut = useCallback(() => {
    if (!isArchonApp || !iframeId || sessionStatus !== 'authenticated') {
      return;
    }
    postSignOutMessage(iframeId, session?.user.id, session?.user.gameSlug);
  }, [
    iframeId,
    isArchonApp,
    session?.user.gameSlug,
    session?.user.id,
    sessionStatus,
  ]);

  useEffect(function handleAppContextMessage() {
    function handleMessage(event: MessageEvent<ArchonAppContextUpdateMessage>) {
      if (event.data.message !== 'app-context-update') {
        return;
      }
      setAppContext(event.data.appContext);
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(
    function queueSignInRequestOnEvent() {
      function handleMessage(event: MessageEvent<ArchonAppLoginMessage>) {
        if (!iframeId || event.data.message !== 'login') {
          return;
        }
        postLoggingMessage(
          iframeId,
          'info',
          '[ArchonAuth] Received sign-in request from Archon App',
          {
            userId: event.data.userId,
            gameSlug: event.data.gameSlug,
          },
        );
        pushSignInRequest(event.data);
      }

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [iframeId, pushSignInRequest],
  );

  useEffect(
    function handleSignInRequest() {
      if (
        !isArchonApp ||
        !iframeId ||
        !signInRequest ||
        sessionStatus === 'loading'
      ) {
        return;
      }
      const { userId, gameSlug, desktopClientToken } = signInRequest;
      if (router.query.gameSlug && gameSlug !== router.query.gameSlug) {
        // Shouldn't sign in to a different gameSlug than the current page
        postLoggingMessage(
          iframeId,
          'warn',
          '[ArchonAuth] Ignoring sign-in request for different gameSlug',
          {
            userId,
            gameSlug,
            routerQueryGameSlug: router.query.gameSlug,
          },
        );
        // eslint-disable-next-line react-hooks/set-state-in-effect
        nextSignInRequest();
        return;
      }

      postLoggingMessage(iframeId, 'info', '[ArchonAuth] Triggering sign-in', {
        userId,
        gameSlug,
        sessionUserId: session?.user.id,
        sessionGameSlug: session?.user.gameSlug,
        routerQueryGameSlug: router.query.gameSlug,
      });

      setIframeRequiresReloadBeforeReady(true);

      signIn('credentials', {
        gameSlug,
        desktopClientToken,
        redirect: false,
      })
        .then(() => {
          postLoggingMessage(
            iframeId,
            'info',
            '[ArchonAuth] Reloading with new session after sign-in',
          );

          const urlObject = new URL(router.asPath, 'http://localhost');
          urlObject.searchParams.set('bypassRouteChangeIndicator', 'true');

          router
            .replace(urlObject.pathname + urlObject.search)
            .catch((error: unknown) => {
              setIframeRequiresReloadBeforeReady(false);
              postLoggingMessage(
                iframeId,
                'error',
                '[ArchonAuth] Error reloading after sign-in',
                error?.toString(),
              );
            });
        })
        .catch((error: unknown) => {
          setIframeRequiresReloadBeforeReady(false);
          postLoggingMessage(
            iframeId,
            'error',
            '[ArchonAuth] Sign-in error',
            error?.toString(),
          );
        })
        .finally(nextSignInRequest);
    },
    [
      nextSignInRequest,
      iframeId,
      isArchonApp,
      session?.user.gameSlug,
      session?.user.id,
      sessionStatus,
      signInRequest,
      router,
    ],
  );

  useEffect(
    function resetIframeReadyTrackingOnNavigation() {
      const handleRouteChange = () => {
        setIframeRequiresReloadBeforeReady(false);
      };
      router.events.on('routeChangeComplete', handleRouteChange);

      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    },
    [router.events],
  );

  useEffect(() => {
    if (
      iframeId &&
      sessionStatus === 'authenticated' &&
      !iframeRequiresReloadBeforeReady
    ) {
      postSessionAuthenticatedMessage(
        iframeId,
        session.user.id,
        session.user.gameSlug,
      );
    }
  }, [
    iframeId,
    session?.user?.id,
    session?.user?.gameSlug,
    sessionStatus,
    iframeRequiresReloadBeforeReady,
  ]);

  useEffect(() => {
    if (iframeId) {
      const [path, hash] = router.asPath.split('#');
      postRouteChangeMessage(iframeId, path, hash ? `#${hash}` : undefined);
    }
  }, [iframeId, router.asPath]);

  useEffect(
    function respondToRouteChangeMessage() {
      async function handleMessage(
        event: MessageEvent<ArchonAppRouteChangeMessage>,
      ) {
        if (event.data.message !== 'route-change') {
          return;
        }

        const { path, hash, index } = event.data;

        const url = new URL(
          hash ? `${path}${hash}` : path,
          window.location.origin,
        );

        const forceReload = url.searchParams.has('forceReload');

        if (!forceReload) {
          // asPath _sometimes_ includes the hash
          const toCompareAgainst = router.asPath.includes('#')
            ? router.asPath
            : window.location.hash
              ? `${router.asPath}${window.location.hash}`
              : router.asPath;

          // url is identical
          if (url.toString().endsWith(toCompareAgainst)) {
            // but we still may want to scroll to the hash.
            if (hash) {
              const newHashChangeKey = [path, hash, index].join('-');

              if (lastHashChangeKey.current !== newHashChangeKey) {
                lastHashChangeKey.current = newHashChangeKey;

                setNavigation((prev) => ({
                  ...prev,
                  hashToNavigateTo: hash,
                }));
              }
            }

            return;
          }
        }

        if (hash) {
          setNavigation((prev) => ({
            ...prev,
            hashToNavigateTo: hash,
          }));
        }

        // In the Archon App, we keep the iframe historyless so that the app can control navigation
        await router.replace(url.toString());
      }

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [router],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent | MouseEvent) => {
      if (iframeId) {
        postKeydownEventMessage(iframeId, event);
      }
    };

    window.addEventListener('keydown', handler);
    window.addEventListener('mousedown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('mousedown', handler);
    };
  }, [iframeId]);

  useEffect(
    function preventPopState() {
      if (!isArchonApp || !iframeId || sessionStatus === 'loading') {
        return;
      }

      router.beforePopState(() => {
        return false;
      });

      return () => {
        router.beforePopState(() => {
          return true;
        });
      };
    },
    [iframeId, isArchonApp, router, sessionStatus],
  );

  useEffect(
    function listenToHashChanges() {
      if (!isArchonApp || !iframeId || sessionStatus === 'loading') {
        return;
      }

      const listener = (event: HashChangeEvent) => {
        const url = new URL(event.newURL);
        const hash = url.hash;

        postRouteChangeMessage(iframeId, null, hash);
      };

      window.addEventListener('hashchange', listener);

      return () => {
        window.removeEventListener('hashchange', listener);
      };
    },
    [iframeId, isArchonApp, sessionStatus, setNavigation],
  );

  const state = useMemo(() => {
    return {
      isArchonApp,
      iframeId,
      appContext,
      triggerSignOut,
    };
  }, [isArchonApp, iframeId, appContext, triggerSignOut]);

  return (
    <ArchonAppIntegrationContext.Provider value={state}>
      {children}
    </ArchonAppIntegrationContext.Provider>
  );
};

function postLoggingMessage(
  iframeId: string,
  level: 'info' | 'warn' | 'error',
  logMessage: string,
  logArgs?: unknown,
) {
  window.top?.postMessage(
    {
      message: 'log',
      iframeId,
      level,
      logMessage,
      logArgs,
    },
    '*',
  );
}

function postSessionAuthenticatedMessage(
  iframeId: string,
  userId: number,
  gameSlug: string,
) {
  window.top?.postMessage(
    {
      message: 'login-success',
      iframeId,
      userId,
      gameSlug,
    },
    '*',
  );
}

function postSignOutMessage(
  iframeId: string,
  userId: number,
  gameSlug: string,
) {
  window.top?.postMessage(
    {
      message: 'sign-out',
      iframeId,
      userId,
      gameSlug,
    } satisfies ArchonAppSignOutMessage,
    '*',
  );
}

function postRouteChangeMessage(
  iframeId: string,
  path: string | null,
  hash?: string,
) {
  window.top?.postMessage(
    {
      message: 'route-change',
      iframeId,
      path,
      hash,
    },
    '*',
  );
}

function postKeydownEventMessage(
  iframeId: string,
  event: KeyboardEvent | MouseEvent,
) {
  window.top?.postMessage(
    {
      message: 'keydown',
      iframeId,
      event: {
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        metaKey: event.metaKey,
        key: 'key' in event ? event.key : undefined,
        button: 'button' in event ? event.button : undefined,
      },
    },
    '*',
  );
}

export const useArchonAppIntegration = (): ArchonAppIntegrationContext =>
  useContext(ArchonAppIntegrationContext);

export type ArchonAppLoginMessage = {
  message: 'login';
  userId: number;
  gameSlug: string;
  desktopClientToken: string;
};

export type ArchonAppSignOutMessage = {
  message: 'sign-out';
  iframeId: string;
  userId: number;
  gameSlug: string;
};

export type ArchonAppContextUpdateMessage = {
  message: 'app-context-update';
  appContext: ArchonAppContext;
};

export type ArchonAppRouteChangeMessage = {
  message: 'route-change';
  path: string;
  hash?: string;
  index?: number;
};

export type ArchonAppFightLinkMessage = {
  message: 'open-fight-link';
  iframeId: string;
  archonGameSlug: string;
  reportCode: string;
  fightId: number;
};

export default useArchonAppIntegration;
